/**
 * App Icon System
 *
 * All icons are pure SVG — no external dependencies.
 * Every icon accepts:
 *   size        — number (px) or CSS string, default 24
 *   strokeWidth — line weight, default 1.5
 *   color       — any CSS color or "currentColor" (default), inherits from parent
 *   className   — additional Tailwind / CSS classes
 */

interface IconProps {
  size?: number | string;
  strokeWidth?: number;
  color?: string;
  className?: string;
}

function base(size: number | string = 24) {
  const s = typeof size === 'number' ? `${size}px` : size;
  return { width: s, height: s, display: 'inline-block', flexShrink: 0 } as const;
}

// ── Navigation / Layout ────────────────────────────────────────────────────────

export function DashboardIcon({
  size = 24,
  strokeWidth = 1,
  color = 'currentColor',
  className,
}: IconProps) {
  return (
    <svg
      style={base(size)}
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
    >
      <path
        d="M10.5 8.75V6.75C10.5 5.10626 10.5 4.28439 10.046 3.73121C9.96291 3.62995 9.87005 3.53709 9.76879 3.45398C9.21561 3 8.39374 3 6.75 3C5.10626 3 4.28439 3 3.73121 3.45398C3.62995 3.53709 3.53709 3.62995 3.45398 3.73121C3 4.28439 3 5.10626 3 6.75V8.75C3 10.3937 3 11.2156 3.45398 11.7688C3.53709 11.8701 3.62995 11.9629 3.73121 12.046C4.28439 12.5 5.10626 12.5 6.75 12.5C8.39374 12.5 9.21561 12.5 9.76879 12.046C9.87005 11.9629 9.96291 11.8701 10.046 11.7688C10.5 11.2156 10.5 10.3937 10.5 8.75Z"
        stroke={color}
        strokeWidth={strokeWidth}
        strokeLinejoin="round"
      />
      <path
        d="M7.75 15.5H5.75C5.05222 15.5 4.70333 15.5 4.41943 15.5861C3.78023 15.78 3.28002 16.2802 3.08612 16.9194C3 17.2033 3 17.5522 3 18.25C3 18.9478 3 19.2967 3.08612 19.5806C3.28002 20.2198 3.78023 20.72 4.41943 20.9139C4.70333 21 5.05222 21 5.75 21H7.75C8.44778 21 8.79667 21 9.08057 20.9139C9.71977 20.72 10.22 20.2198 10.4139 19.5806C10.5 19.2967 10.5 18.9478 10.5 18.25C10.5 17.5522 10.5 17.2033 10.4139 16.9194C10.22 16.2802 9.71977 15.78 9.08057 15.5861C8.79667 15.5 8.44778 15.5 7.75 15.5Z"
        stroke={color}
        strokeWidth={strokeWidth}
        strokeLinejoin="round"
      />
      <path
        d="M21 17.25V15.25C21 13.6063 21 12.7844 20.546 12.2312C20.4629 12.1299 20.3701 12.0371 20.2688 11.954C19.7156 11.5 18.8937 11.5 17.25 11.5C15.6063 11.5 14.7844 11.5 14.2312 11.954C14.1299 12.0371 14.0371 12.1299 13.954 12.2312C13.5 12.7844 13.5 13.6063 13.5 15.25V17.25C13.5 18.8937 13.5 19.7156 13.954 20.2688C14.0371 20.3701 14.1299 20.4629 14.2312 20.546C14.7844 21 15.6063 21 17.25 21C18.8937 21 19.7156 21 20.2688 20.546C20.3701 20.4629 20.4629 20.3701 20.546 20.2688C21 19.7156 21 18.8937 21 17.25Z"
        stroke={color}
        strokeWidth={strokeWidth}
        strokeLinejoin="round"
      />
      <path
        d="M18.25 3H16.25C15.5522 3 15.2033 3 14.9194 3.08612C14.2802 3.28002 13.78 3.78023 13.5861 4.41943C13.5 4.70333 13.5 5.05222 13.5 5.75C13.5 6.44778 13.5 6.79667 13.5861 7.08057C13.78 7.71977 14.2802 8.21998 14.9194 8.41388C15.2033 8.5 15.5522 8.5 16.25 8.5H18.25C18.9478 8.5 19.2967 8.5 19.5806 8.41388C20.2198 8.21998 20.72 7.71977 20.9139 7.08057C21 6.79667 21 6.44778 21 5.75C21 5.05222 21 4.70333 20.9139 4.41943C20.72 3.78023 20.2198 3.28002 19.5806 3.08612C19.2967 3 18.9478 3 18.25 3Z"
        stroke={color}
        strokeWidth={strokeWidth}
        strokeLinejoin="round"
      />
    </svg>
  );
}

export function SettingsIcon({
  size = 24,
  strokeWidth = 1,
  color = 'currentColor',
  className,
}: IconProps) {
  return (
    <svg
      style={base(size)}
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
    >
      <path
        d="M21.3175 7.14139L20.8239 6.28479C20.4506 5.63696 20.264 5.31305 19.9464 5.18388C19.6288 5.05472 19.2696 5.15664 18.5513 5.36048L17.3311 5.70418C16.8725 5.80994 16.3913 5.74994 15.9726 5.53479L15.6357 5.34042C15.2766 5.11043 15.0004 4.77133 14.8475 4.37274L14.5136 3.37536C14.294 2.71534 14.1842 2.38533 13.9228 2.19657C13.6615 2.00781 13.3143 2.00781 12.6199 2.00781H11.5051C10.8108 2.00781 10.4636 2.00781 10.2022 2.19657C9.94085 2.38533 9.83106 2.71534 9.61149 3.37536L9.27753 4.37274C9.12465 4.77133 8.84845 5.11043 8.48937 5.34042L8.15249 5.53479C7.73374 5.74994 7.25259 5.80994 6.79398 5.70418L5.57375 5.36048C4.85541 5.15664 4.49625 5.05472 4.17867 5.18388C3.86109 5.31305 3.67445 5.63696 3.30115 6.28479L2.80757 7.14139C2.45766 7.74864 2.2827 8.05227 2.31666 8.37549C2.35061 8.69871 2.58483 8.95918 3.05326 9.48012L4.0843 10.6328C4.3363 10.9518 4.51521 11.5078 4.51521 12.0077C4.51521 12.5078 4.33636 13.0636 4.08433 13.3827L3.05326 14.5354C2.58483 15.0564 2.35062 15.3168 2.31666 15.6401C2.2827 15.9633 2.45766 16.2669 2.80757 16.8741L3.30114 17.7307C3.67443 18.3785 3.86109 18.7025 4.17867 18.8316C4.49625 18.9608 4.85542 18.8589 5.57377 18.655L6.79394 18.3113C7.25263 18.2055 7.73387 18.2656 8.15267 18.4808L8.4895 18.6752C8.84851 18.9052 9.12464 19.2442 9.2775 19.6428L9.61149 20.6403C9.83106 21.3003 9.94085 21.6303 10.2022 21.8191C10.4636 22.0078 10.8108 22.0078 11.5051 22.0078H12.6199C13.3143 22.0078 13.6615 22.0078 13.9228 21.8191C14.1842 21.6303 14.294 21.3003 14.5136 20.6403L14.8476 19.6428C15.0004 19.2442 15.2765 18.9052 15.6356 18.6752L15.9724 18.4808C16.3912 18.2656 16.8724 18.2055 17.3311 18.3113L18.5513 18.655C19.2696 18.8589 19.6288 18.9608 19.9464 18.8316C20.264 18.7025 20.4506 18.3785 20.8239 17.7307L21.3175 16.8741C21.6674 16.2669 21.8423 15.9633 21.8084 15.6401C21.7744 15.3168 21.5402 15.0564 21.0718 14.5354L20.0407 13.3827C19.7887 13.0636 19.6098 12.5078 19.6098 12.0077C19.6098 11.5078 19.7888 10.9518 20.0407 10.6328L21.0718 9.48012C21.5402 8.95918 21.7744 8.69871 21.8084 8.37549C21.8423 8.05227 21.6674 7.74864 21.3175 7.14139Z"
        stroke={color}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
      />
      <path
        d="M15.5195 12C15.5195 13.933 13.9525 15.5 12.0195 15.5C10.0865 15.5 8.51953 13.933 8.51953 12C8.51953 10.067 10.0865 8.5 12.0195 8.5C13.9525 8.5 15.5195 10.067 15.5195 12Z"
        stroke={color}
        strokeWidth={strokeWidth}
      />
    </svg>
  );
}

// ── AI / Content ───────────────────────────────────────────────────────────────

export function SparkleIcon({
  size = 24,
  strokeWidth = 1,
  color = 'currentColor',
  className,
}: IconProps) {
  return (
    <svg
      style={base(size)}
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
    >
      <path
        d="M15 2L15.5387 4.39157C15.9957 6.42015 17.5798 8.00431 19.6084 8.46127L22 9L19.6084 9.53873C17.5798 9.99569 15.9957 11.5798 15.5387 13.6084L15 16L14.4613 13.6084C14.0043 11.5798 12.4202 9.99569 10.3916 9.53873L8 9L10.3916 8.46127C12.4201 8.00431 14.0043 6.42015 14.4613 4.39158L15 2Z"
        stroke={color}
        strokeWidth={strokeWidth}
        strokeLinejoin="round"
      />
      <path
        d="M7 12L7.38481 13.7083C7.71121 15.1572 8.84275 16.2888 10.2917 16.6152L12 17L10.2917 17.3848C8.84275 17.7112 7.71121 18.8427 7.38481 20.2917L7 22L6.61519 20.2917C6.28879 18.8427 5.15725 17.7112 3.70827 17.3848L2 17L3.70827 16.6152C5.15725 16.2888 6.28879 15.1573 6.61519 13.7083L7 12Z"
        stroke={color}
        strokeWidth={strokeWidth}
        strokeLinejoin="round"
      />
    </svg>
  );
}

export function PenToolIcon({
  size = 24,
  strokeWidth = 1,
  color = 'currentColor',
  className,
}: IconProps) {
  return (
    <svg
      style={base(size)}
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
    >
      <path
        d="M14.5995 20.4737L7.63427 21.7672C6.2983 22.0153 5.63031 22.1393 5.24549 21.7545C4.86067 21.3697 4.98471 20.7016 5.2328 19.3656L6.52621 12.4001C6.73362 11.2831 6.83732 10.7246 7.20549 10.3872C7.57365 10.0497 8.24697 9.98389 9.59359 9.85218C10.8915 9.72524 12.1197 9.28032 13.4 8L19 13.6005C17.7197 14.8808 17.2746 16.1083 17.1474 17.4062C17.0155 18.753 16.9495 19.4264 16.6121 19.7945C16.2747 20.1626 15.7163 20.2663 14.5995 20.4737Z"
        stroke={color}
        strokeWidth={strokeWidth}
        strokeLinejoin="round"
      />
      <path
        d="M13 16.2105C12.4405 16.1197 11.9289 15.8763 11.5263 15.4737M11.5263 15.4737C11.1237 15.0711 10.8803 14.5595 10.7895 14M11.5263 15.4737L6 21"
        stroke={color}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
      />
      <path
        d="M13.5 8C14.1332 7.06586 15.4907 5.16107 16.7613 5.00976C17.6287 4.90648 18.3472 5.62499 19.7842 7.06202L19.938 7.2158C21.375 8.65283 22.0935 9.37135 21.9902 10.2387C21.8389 11.5092 19.9341 12.8668 19 13.5"
        stroke={color}
        strokeWidth={strokeWidth}
        strokeLinejoin="round"
      />
      <path d="M5 8L5 2M2 5H8" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" />
    </svg>
  );
}

// ── Data / Analytics ───────────────────────────────────────────────────────────

export function BarChartIcon({
  size = 24,
  strokeWidth = 1,
  color = 'currentColor',
  className,
}: IconProps) {
  return (
    <svg
      style={base(size)}
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
    >
      <path
        d="M10 17H9C8.44772 17 8 16.5523 8 16L8 10C8 9.44772 8.44772 9 9 9L10 9C10.5523 9 11 9.44772 11 10L11 16C11 16.5523 10.5523 17 10 17Z"
        stroke={color}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M17 17H16C15.4477 17 15 16.5523 15 16L15 6C15 5.44772 15.4477 5 16 5L17 5C17.5523 5 18 5.44771 18 6L18 16C18 16.5523 17.5523 17 17 17Z"
        stroke={color}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M3 3V13C3 16.7712 3 18.6569 4.17157 19.8284C5.34315 21 7.22876 21 11 21H21"
        stroke={color}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export function RssFeedIcon({
  size = 24,
  strokeWidth = 1,
  color = 'currentColor',
  className,
}: IconProps) {
  return (
    <svg
      style={base(size)}
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
    >
      {/* Top-right circle with inner signal lines — filled paths recoloured via color */}
      <path
        d="M15.6563 6.87864C15.2824 6.70029 14.8348 6.85877 14.6564 7.23263C14.4781 7.60648 14.6366 8.05412 15.0104 8.23247L15.6563 6.87864ZM16.4444 8.66667L15.7983 9.04739C15.9389 9.28613 16.1998 9.42786 16.4766 9.41598C16.7534 9.4041 17.0012 9.24055 17.1209 8.99064L16.4444 8.66667ZM19.0222 6.54928C19.3869 6.35294 19.5234 5.8981 19.3271 5.53338C19.1307 5.16866 18.6759 5.03216 18.3112 5.2285L19.0222 6.54928ZM21.25 7C21.25 9.34721 19.3472 11.25 17 11.25V12.75C20.1756 12.75 22.75 10.1756 22.75 7H21.25ZM17 11.25C14.6528 11.25 12.75 9.34721 12.75 7H11.25C11.25 10.1756 13.8244 12.75 17 12.75V11.25ZM12.75 7C12.75 4.65279 14.6528 2.75 17 2.75V1.25C13.8244 1.25 11.25 3.82436 11.25 7H12.75ZM17 2.75C19.3472 2.75 21.25 4.65279 21.25 7H22.75C22.75 3.82436 20.1756 1.25 17 1.25V2.75ZM15.3333 7.55556C15.0104 8.23247 15.0102 8.23236 15.0099 8.23224C15.0098 8.2322 15.0096 8.23209 15.0094 8.23201C15.0091 8.23187 15.0088 8.23172 15.0085 8.23158C15.008 8.2313 15.0074 8.23104 15.0069 8.23079C15.0059 8.23029 15.005 8.22987 15.0043 8.2295C15.0028 8.22878 15.0019 8.22832 15.0015 8.22814C15.0008 8.22777 15.0022 8.22849 15.0056 8.23033C15.0125 8.23401 15.0271 8.24214 15.0482 8.25504C15.0904 8.28092 15.1574 8.32538 15.2383 8.39087C15.4003 8.52206 15.6137 8.73411 15.7983 9.04739L17.0906 8.28594C16.799 7.79101 16.4568 7.4475 16.1823 7.22519C16.045 7.11393 15.9235 7.03216 15.8318 6.97599C15.7859 6.94786 15.7472 6.92601 15.7174 6.90997C15.7026 6.90195 15.6899 6.89537 15.6796 6.89017C15.6745 6.88757 15.67 6.88531 15.6661 6.88339C15.6641 6.88244 15.6623 6.88156 15.6607 6.88077C15.6599 6.88037 15.6591 6.88 15.6584 6.87964C15.658 6.87946 15.6576 6.87929 15.6573 6.87912C15.6571 6.87904 15.6569 6.87892 15.6568 6.87888C15.6565 6.87876 15.6563 6.87864 15.3333 7.55556ZM16.4444 8.66667C17.1209 8.99064 17.1208 8.99074 17.1208 8.99084C17.1208 8.99086 17.1207 8.99095 17.1207 8.99099C17.1207 8.99108 17.1206 8.99113 17.1206 8.99115C17.1206 8.9912 17.1206 8.99112 17.1207 8.99091C17.1209 8.9905 17.1214 8.98959 17.1221 8.98819C17.1234 8.98539 17.1257 8.98065 17.129 8.97409C17.1354 8.96095 17.1456 8.94051 17.1593 8.91364C17.1866 8.85985 17.2278 8.78056 17.2811 8.68269C17.3883 8.4862 17.5425 8.2186 17.7307 7.93376C18.1295 7.33017 18.5986 6.77731 19.0222 6.54928L18.3112 5.2285C17.5331 5.64734 16.8911 6.48337 16.4792 7.10695C16.262 7.43569 16.086 7.74125 15.9642 7.96452C15.9032 8.07653 15.8552 8.16878 15.822 8.23405C15.8054 8.26671 15.7925 8.29268 15.7834 8.31107C15.7789 8.32027 15.7753 8.32757 15.7728 8.33288C15.7715 8.33554 15.7704 8.33769 15.7696 8.33933C15.7692 8.34015 15.7689 8.34084 15.7686 8.3414C15.7685 8.34168 15.7684 8.34193 15.7683 8.34215C15.7682 8.34225 15.7682 8.34239 15.7681 8.34245C15.7681 8.34257 15.768 8.3427 16.4444 8.66667Z"
        fill={color}
      />
      {/* Rounded rectangle body + RSS arcs — filled path recoloured via color */}
      <path
        d="M22.75 13.4937C22.75 13.0795 22.4142 12.7437 22 12.7437C21.5858 12.7437 21.25 13.0795 21.25 13.4937H22.75ZM20.5342 20.7543L21.0198 21.3258V21.3258L20.5342 20.7543ZM3.44705 6.23317L2.96137 5.66166H2.96137L3.44705 6.23317ZM12.9915 21.25C12.5773 21.25 12.2415 21.5858 12.2415 22C12.2415 22.4142 12.5773 22.75 12.9915 22.75V21.25ZM13.9925 21.9956L13.9871 21.2456L13.9806 21.2457L13.9925 21.9956ZM1.25025 10.9727C1.23966 11.3868 1.56675 11.731 1.98083 11.7416C2.39491 11.7522 2.73917 11.4251 2.74975 11.0111L1.25025 10.9727ZM9.26038 5.74991C9.67455 5.74352 10.0051 5.4026 9.99873 4.98844C9.99234 4.57427 9.65142 4.2437 9.23725 4.25009L9.26038 5.74991ZM21.25 13.4937C21.25 15.5235 21.2478 16.9573 21.076 18.0434C20.9098 19.0938 20.5995 19.7145 20.0485 20.1828L21.0198 21.3258C21.9347 20.5483 22.3573 19.5438 22.5576 18.2778C22.7522 17.0475 22.75 15.4739 22.75 13.4937H21.25ZM12.9915 22.75C13.3534 22.75 13.6053 22.7489 13.7674 22.7478C13.8485 22.7472 13.9071 22.7466 13.9457 22.7462C13.9651 22.746 13.9794 22.7458 13.9891 22.7457C13.9939 22.7456 13.9976 22.7456 14.0001 22.7455C14.0014 22.7455 14.0023 22.7455 14.0031 22.7455C14.0034 22.7455 14.0037 22.7455 14.0039 22.7455C14.004 22.7455 14.0041 22.7455 14.0042 22.7455C14.0042 22.7455 14.0043 22.7455 14.0043 22.7455C14.0043 22.7455 14.0043 22.7455 14.0043 22.7455C14.0043 22.7455 14.0044 22.7455 13.9925 21.9956C13.9806 21.2457 13.9806 21.2457 13.9806 21.2457C13.9806 21.2457 13.9807 21.2457 13.9807 21.2457C13.9807 21.2457 13.9807 21.2457 13.9807 21.2457C13.9807 21.2457 13.9807 21.2457 13.9806 21.2457C13.9806 21.2457 13.9805 21.2457 13.9803 21.2457C13.9799 21.2457 13.9792 21.2457 13.9782 21.2457C13.9763 21.2457 13.9732 21.2458 13.969 21.2458C13.9605 21.2459 13.9473 21.2461 13.9291 21.2463C13.8927 21.2467 13.8361 21.2473 13.757 21.2478C13.5988 21.2489 13.3503 21.25 12.9915 21.25V22.75ZM13.9979 22.7455C15.7037 22.7333 17.0832 22.6875 18.2019 22.5013C19.3334 22.313 20.2597 21.9718 21.0198 21.3258L20.0485 20.1828C19.5666 20.5923 18.9303 20.8594 17.9555 21.0217C16.9679 21.1861 15.6976 21.2333 13.9871 21.2456L13.9979 22.7455ZM2.74975 11.0111C2.81163 8.59113 3.08942 7.52134 3.93273 6.80467L2.96137 5.66166C1.55911 6.85335 1.31136 8.58282 1.25025 10.9727L2.74975 11.0111ZM9.23725 4.25009C6.33117 4.2949 4.34751 4.48368 2.96137 5.66166L3.93273 6.80467C4.84512 6.02929 6.25834 5.79621 9.26038 5.74991L9.23725 4.25009Z"
        fill={color}
      />
      {/* RSS dot */}
      <path
        d="M2.98242 21H2.9914"
        stroke={color}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      {/* RSS arcs (inner + outer) */}
      <path
        d="M2 17.2349C4.49328 17.2349 6.77053 19.5 6.77053 21.9996M10 21.9996C10 17.5 5.99511 14 2.04522 14"
        stroke={color}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
      />
    </svg>
  );
}

// ── People / Social ────────────────────────────────────────────────────────────

export function UserSquareIcon({
  size = 24,
  strokeWidth = 1,
  color = 'currentColor',
  className,
}: IconProps) {
  return (
    <svg
      style={base(size)}
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
    >
      <path
        d="M11.5001 2.5L12.5001 2.50001C16.7427 2.50006 18.864 2.50008 20.182 3.8181C21.5 5.13611 21.5 7.25741 21.5 11.5V12.5001C21.5 16.7427 21.5 18.8641 20.182 20.1821C18.864 21.5001 16.7426 21.5001 12.5 21.5001H11.5C7.25736 21.5001 5.13604 21.5001 3.81802 20.1821C2.5 18.8641 2.5 16.7427 2.5 12.5001V11.5C2.5 7.25732 2.5 5.13598 3.81804 3.81795C5.13607 2.49993 7.25741 2.49995 11.5001 2.5Z"
        stroke={color}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M11.9999 7.39502C13.9329 7.39502 15.4999 8.96202 15.4999 10.895C15.4999 12.828 13.9329 14.395 11.9999 14.395C10.0669 14.395 8.49988 12.828 8.49988 10.895C8.49988 8.96202 10.0669 7.39502 11.9999 7.39502Z"
        stroke={color}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M18.9823 20.895C18.7259 17.2624 15.6978 14.395 11.9999 14.395C8.302 14.395 5.27383 17.2624 5.01746 20.895"
        stroke={color}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export function ChatIcon({
  size = 24,
  strokeWidth = 1,
  color = 'currentColor',
  className,
}: IconProps) {
  return (
    <svg
      style={base(size)}
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
    >
      <path
        d="M12.0045 11H12.0135M8.00903 11H8.018"
        stroke={color}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M12 3C10.5209 3 9.09517 3.0309 7.7562 3.08819C5.3157 3.1926 4.09545 3.24481 3.13007 4.21745C2.16469 5.19009 2.12282 6.37683 2.03909 8.7503C2.01346 9.47679 2 10.2292 2 11C2 11.7708 2.01346 12.5232 2.03909 13.2497C2.12282 15.6232 2.16469 16.8099 3.13007 17.7825C4.09545 18.7552 5.31569 18.8074 7.75619 18.9118C7.83715 18.9153 7.91842 18.9186 8 18.9219V21.2701C8 21.6732 8.32679 22 8.72991 22C8.90419 22 9.07273 21.9376 9.20503 21.8242L11.3845 19.9553C11.9325 19.4855 12.2064 19.2506 12.532 19.1266C12.8576 19.0026 13.2282 18.9955 13.9693 18.9815C14.7498 18.9667 15.5098 18.9432 16.2437 18.9118C18.6843 18.8074 19.9046 18.7552 20.8699 17.7826C21.8353 16.8099 21.8772 15.6232 21.9609 13.2497C21.9865 12.5232 22 11.7708 22 11C22 10.2292 21.9865 9.47679 21.9609 8.7503C21.9372 8.07936 21.9169 7.50325 21.8781 7"
        stroke={color}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M15 5.33333C15 5.33333 16 5.33333 17 7C17 7 19.1765 2.83333 22 2"
        stroke={color}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export function TrashIcon({
  size = 24,
  strokeWidth = 1,
  color = 'currentColor',
  className,
}: IconProps) {
  return (
    <svg
      style={base(size)}
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
    >
      <path
        d="M19.5 5.5L18.8803 15.5251C18.7219 18.0864 18.6428 19.3671 18.0008 20.2879C17.6833 20.7431 17.2747 21.1273 16.8007 21.416C15.8421 22 14.559 22 11.9927 22C9.42312 22 8.1383 22 7.17905 21.4149C6.7048 21.1257 6.296 20.7408 5.97868 20.2848C5.33688 19.3626 5.25945 18.0801 5.10461 15.5152L4.5 5.5"
        stroke={color}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
      ></path>
      <path d="M9 11.7349H15" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round"></path>
      <path
        d="M10.5 15.6543H13.5"
        stroke={color}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
      ></path>
      <path
        d="M3 5.5H21M16.0555 5.5L15.3729 4.09173C14.9194 3.15626 14.6926 2.68852 14.3015 2.39681C14.2148 2.3321 14.1229 2.27454 14.0268 2.2247C13.5937 2 13.0739 2 12.0343 2C10.9686 2 10.4358 2 9.99549 2.23412C9.89791 2.28601 9.80479 2.3459 9.7171 2.41317C9.32145 2.7167 9.10044 3.20155 8.65842 4.17126L8.05273 5.5"
        stroke={color}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
      ></path>
    </svg>
  );
}

export function LightbulbIcon({
  size = 24,
  strokeWidth = 1,
  color = 'currentColor',
  className,
}: IconProps) {
  return (
    <svg
      style={base(size)}
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
    >
      <path
        d="M6.08938 14.9992C5.71097 14.1486 5.5 13.2023 5.5 12.2051C5.5 8.50154 8.41015 5.49921 12 5.49921C15.5899 5.49921 18.5 8.50154 18.5 12.2051C18.5 13.2023 18.289 14.1486 17.9106 14.9992"
        stroke={color}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
      ></path>
      <path
        d="M12 1.99921V2.99921"
        stroke={color}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
      ></path>
      <path
        d="M22 11.9992H21"
        stroke={color}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
      ></path>
      <path
        d="M3 11.9992H2"
        stroke={color}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
      ></path>
      <path
        d="M19.0704 4.92792L18.3633 5.63503"
        stroke={color}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
      ></path>
      <path
        d="M5.6368 5.636L4.92969 4.92889"
        stroke={color}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
      ></path>
      <path
        d="M14.517 19.3056C15.5274 18.9788 15.9326 18.054 16.0466 17.1238C16.0806 16.8459 15.852 16.6154 15.572 16.6154L8.47685 16.6156C8.18725 16.6156 7.95467 16.8614 7.98925 17.1489C8.1009 18.0773 8.3827 18.7555 9.45345 19.3056M14.517 19.3056C14.517 19.3056 9.62971 19.3056 9.45345 19.3056M14.517 19.3056C14.3955 21.2506 13.8338 22.0209 12.0068 21.9993C10.0526 22.0354 9.60303 21.0833 9.45345 19.3056"
        stroke={color}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
      ></path>
    </svg>
  );
}

export function CalendarIcon({
  size = 24,
  strokeWidth = 1,
  color = 'currentColor',
  className,
}: IconProps) {
  return (
    <svg
      style={base(size)}
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
    >
      <path
        d="M16 2V6M8 2V6"
        stroke={color}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
      ></path>
      <path
        d="M13 4H11C7.22876 4 5.34315 4 4.17157 5.17157C3 6.34315 3 8.22876 3 12V14C3 17.7712 3 19.6569 4.17157 20.8284C5.34315 22 7.22876 22 11 22H13C16.7712 22 18.6569 22 19.8284 20.8284C21 19.6569 21 17.7712 21 14V12C21 8.22876 21 6.34315 19.8284 5.17157C18.6569 4 16.7712 4 13 4Z"
        stroke={color}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
      ></path>
      <path
        d="M3 10H21"
        stroke={color}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
      ></path>
      <path
        d="M10 18.5002L9.99999 13.8474C9.99999 13.6557 9.86325 13.5002 9.69458 13.5002H9M14 18.4983L15.4855 13.8923C15.4951 13.8626 15.5 13.8315 15.5 13.8002C15.5 13.6346 15.3657 13.5002 15.2 13.5002L13 13.5"
        stroke={color}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
      ></path>
    </svg>
  );
}

export function ChatAIIcon({
  size = 24,
  strokeWidth = 1,
  color = 'currentColor',
  className,
}: IconProps) {
  return (
    <svg
      style={base(size)}
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
    >
      <path
        d="M14.1706 20.8905C18.3536 20.6125 21.6856 17.2332 21.9598 12.9909C22.0134 12.1607 22.0134 11.3009 21.9598 10.4707C21.6856 6.22838 18.3536 2.84913 14.1706 2.57107C12.7435 2.47621 11.2536 2.47641 9.8294 2.57107C5.64639 2.84913 2.31441 6.22838 2.04024 10.4707C1.98659 11.3009 1.98659 12.1607 2.04024 12.9909C2.1401 14.536 2.82343 15.9666 3.62791 17.1746C4.09501 18.0203 3.78674 19.0758 3.30021 19.9978C2.94941 20.6626 2.77401 20.995 2.91484 21.2351C3.05568 21.4752 3.37026 21.4829 3.99943 21.4982C5.24367 21.5285 6.08268 21.1757 6.74868 20.6846C7.1264 20.4061 7.31527 20.2668 7.44544 20.2508C7.5756 20.2348 7.83177 20.3403 8.34401 20.5513C8.8044 20.7409 9.33896 20.8579 9.8294 20.8905C11.2536 20.9852 12.7435 20.9854 14.1706 20.8905Z"
        stroke={color}
        strokeWidth={strokeWidth}
        strokeLinejoin="round"
      ></path>
      <path
        d="M7.5 15L9.34189 9.47434C9.43631 9.19107 9.7014 9 10 9C10.2986 9 10.5637 9.19107 10.6581 9.47434L12.5 15M15.5 9V15M8.5 13H11.5"
        stroke={color}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
      ></path>
    </svg>
  );
}

export function FloppyIcon({
  size = 24,
  strokeWidth = 1,
  color = 'currentColor',
  className,
}: IconProps) {
  return (
    <svg
      style={base(size)}
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
    >
      <path
        d="M8 22V19C8 17.1144 8 16.1716 8.58579 15.5858C9.17157 15 10.1144 15 12 15C13.8856 15 14.8284 15 15.4142 15.5858C16 16.1716 16 17.1144 16 19V22"
        stroke={color}
        strokeWidth={strokeWidth}
        strokeLinejoin="round"
      ></path>
      <path
        d="M10 7H14"
        stroke={color}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
      ></path>
      <path
        d="M3 11.8584C3 7.28199 3 4.99376 4.38674 3.54394C4.43797 3.49038 4.49038 3.43797 4.54394 3.38674C5.99376 2 8.28199 2 12.8584 2C13.943 2 14.4655 2.00376 14.9628 2.18936C15.4417 2.3681 15.8429 2.70239 16.6452 3.37099L18.8411 5.20092C19.9027 6.08561 20.4335 6.52795 20.7168 7.13266C21 7.73737 21 8.42833 21 9.81025V13C21 16.7497 21 18.6246 20.0451 19.9389C19.7367 20.3634 19.3634 20.7367 18.9389 21.0451C17.6246 22 15.7497 22 12 22C8.25027 22 6.3754 22 5.06107 21.0451C4.6366 20.7367 4.26331 20.3634 3.95491 19.9389C3 18.6246 3 16.7497 3 13V11.8584Z"
        stroke={color}
        strokeWidth={strokeWidth}
      ></path>
    </svg>
  );
}

export function SendIcon({
  size = 24,
  strokeWidth = 1,
  color = 'currentColor',
  className,
}: IconProps) {
  return (
    <svg
      style={base(size)}
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
    >
      <path
        d="M21.0477 3.05293C18.8697 0.707363 2.48648 6.4532 2.50001 8.551C2.51535 10.9299 8.89809 11.6617 10.6672 12.1581C11.7311 12.4565 12.016 12.7625 12.2613 13.8781C13.3723 18.9305 13.9301 21.4435 15.2014 21.4996C17.2278 21.5892 23.1733 5.342 21.0477 3.05293Z"
        stroke={color}
        strokeWidth={strokeWidth}
      ></path>
      <path
        d="M11.4999 12.5L14.9999 9"
        stroke={color}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
      ></path>
    </svg>
  );
}

export function DigitalClockIcon({
  size = 24,
  strokeWidth = 1,
  color = 'currentColor',
  className,
}: IconProps) {
  return (
    <svg
      style={base(size)}
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
    >
      <path
        d="M5 19.0001V21.0001M19 19.0001V21.0001"
        stroke={color}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
      ></path>
      <path
        d="M16 6H8C5.17157 6 3.75736 6 2.87868 6.87868C2 7.75736 2 9.17157 2 12V13C2 15.8284 2 17.2426 2.87868 18.1213C3.75736 19 5.17157 19 8 19H16C18.8284 19 20.2426 19 21.1213 18.1213C22 17.2426 22 15.8284 22 13V12C22 9.17157 22 7.75736 21.1213 6.87868C20.2426 6 18.8284 6 16 6Z"
        stroke={color}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
      ></path>
      <path
        d="M7 6L7.0883 5.73509C7.52832 4.41505 7.74832 3.75503 8.2721 3.37752C8.79587 3 9.49159 3 10.883 3H13.117C14.5084 3 15.2041 3 15.7279 3.37752C16.2517 3.75503 16.4717 4.41505 16.9117 5.73509L17 6"
        stroke={color}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
      ></path>
      <path
        d="M19 13.75V11.25C19 10.5597 18.4404 10 17.75 10C17.0596 10 16.5 10.5597 16.5 11.25V13.75C16.5 14.4404 17.0596 15 17.75 15C18.4404 15 19 14.4404 19 13.75Z"
        stroke={color}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
      ></path>
      <path
        d="M13.5 13.75V11.25C13.5 10.5596 12.9404 10 12.25 10C11.5596 10 11 10.5596 11 11.25V13.75C11 14.4404 11.5596 15 12.25 15C12.9404 15 13.5 14.4403 13.5 13.75Z"
        stroke={color}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
      ></path>
      <path
        d="M5 15V10"
        stroke={color}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
      ></path>
      <path
        d="M8.05039 11.0004H8.00039M8.05078 14.0004H8.00078M8.10039 11.0004C8.10039 11.0556 8.05562 11.1004 8.00039 11.1004C7.94516 11.1004 7.90039 11.0556 7.90039 11.0004C7.90039 10.9452 7.94516 10.9004 8.00039 10.9004C8.05562 10.9004 8.10039 10.9452 8.10039 11.0004ZM8.10078 14.0004C8.10078 14.0556 8.05601 14.1004 8.00078 14.1004C7.94555 14.1004 7.90078 14.0556 7.90078 14.0004C7.90078 13.9452 7.94555 13.9004 8.00078 13.9004C8.05601 13.9004 8.10078 13.9452 8.10078 14.0004Z"
        stroke={color}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
      ></path>
    </svg>
  );
}

export function CalendarRemoveIcon({
  size = 24,
  strokeWidth = 1,
  color = 'currentColor',
  className,
}: IconProps) {
  return (
    <svg
      style={base(size)}
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
    >
      <path
        d="M16 2V6M8 2V6"
        stroke={color}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
      ></path>
      <path
        d="M21 13V12C21 8.22876 21 6.34315 19.8284 5.17157C18.6569 4 16.7712 4 13 4H11C7.22876 4 5.34315 4 4.17157 5.17157C3 6.34315 3 8.22876 3 12V14C3 17.7712 3 19.6569 4.17157 20.8284C5.34315 22 7.22876 22 11 22H12"
        stroke={color}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
      ></path>
      <path
        d="M3 10H21"
        stroke={color}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
      ></path>
      <path
        d="M21 16L18 19M18 19L15 22M18 19L21 22M18 19L15 16"
        stroke={color}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
      ></path>
    </svg>
  );
}

export function CalendarSyncIcon({
  size = 24,
  strokeWidth = 1,
  color = 'currentColor',
  className,
}: IconProps) {
  return (
    <svg
      style={base(size)}
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
    >
      <path
        d="M16 2V6M8 2V6"
        stroke={color}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
      ></path>
      <path
        d="M12 22H11C7.22876 22 5.34315 22 4.17157 20.8284C3 19.6569 3 17.7712 3 14V12C3 8.22876 3 6.34315 4.17157 5.17157C5.34315 4 7.22876 4 11 4H13C16.7712 4 18.6569 4 19.8284 5.17157C20.8915 6.23467 20.99 7.8857 20.9991 11"
        stroke={color}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
      ></path>
      <path
        d="M3 10H21"
        stroke={color}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
      ></path>
      <path
        d="M14.385 15.7855C15.0271 14.4342 16.4044 13.5 18 13.5C19.4806 13.5 20.7733 14.3044 21.4649 15.5M14.385 15.7855C14.6833 16 15.1502 16 16 16H17M14.385 15.7855C14.3524 15.762 14.3218 15.736 14.2929 15.7071C14 15.4142 14 14.9428 14 14V13M21.615 19.2145C20.9729 20.5658 19.5956 21.5 18 21.5C16.5194 21.5 15.2267 20.6956 14.5351 19.5M21.615 19.2145C21.3166 19 20.8498 19 20 19H19M21.615 19.2145C21.6476 19.238 21.6782 19.264 21.7071 19.2929C22 19.5858 22 20.0572 22 21V22"
        stroke={color}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
      ></path>
    </svg>
  );
}

export function CalendarsIcon({
  size = 24,
  strokeWidth = 1,
  color = 'currentColor',
  className,
}: IconProps) {
  return (
    <svg
      style={base(size)}
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
    >
      <path
        d="M8 11.5002V9.50024C8 6.67182 8 5.2576 8.87868 4.37892C9.75736 3.50024 11.1716 3.50024 14 3.50024H16C18.8284 3.50024 20.2426 3.50024 21.1213 4.37892C22 5.2576 22 6.67182 22 9.50024V11.5002C22 14.3287 22 15.7429 21.1213 16.6216C20.2426 17.5002 18.8284 17.5002 16 17.5002H14C11.1716 17.5002 9.75736 17.5002 8.87868 16.6216C8 15.7429 8 14.3287 8 11.5002Z"
        stroke={color}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
      ></path>
      <path
        d="M4.5 8.14136C3.79231 8.25986 3.27972 8.47786 2.87868 8.8789C2 9.75758 2 11.1718 2 14.0002V16.0002C2 18.8286 2 20.2429 2.87868 21.1215C3.75736 22.0002 5.17157 22.0002 8 22.0002H10C12.8284 22.0002 14.2426 22.0002 15.1213 21.1215C15.1607 21.0822 15.1982 21.0418 15.2341 21.0002"
        stroke={color}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
      ></path>
      <path
        d="M8 9.00024H22"
        stroke={color}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
      ></path>
      <path
        d="M12 2.00024V5.00024M18 2.00024V5.00024"
        stroke={color}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
      ></path>
      <path
        d="M2 13.5002H4.5"
        stroke={color}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
      ></path>
    </svg>
  );
}
